import type { EditorHost } from '@blocksuite/block-std';
import type {
  DatabaseBlockComponent,
  DatabaseBlockModel,
  NoteBlockModel,
} from '@blocksuite/blocks';
import { matchFlavours, NoteDisplayMode } from '@blocksuite/blocks';
import type { BlockModel, Doc } from '@blocksuite/store';
import type { SerializedAttendee } from '@pique/core/modules/properties/services/schema';

import type { AuthService } from '../../../../modules/cloud';
import type { WorkspacePropertiesAdapter } from '../../../../modules/properties';
import type { ActionItemsService } from '../../../../modules/properties/services/action-items-service';
import {
  createAttendeeFromAuthUser,
  normalizeEmail,
} from '../../../../modules/properties/services/attendee-id-utils';
import type { AttendeesService } from '../../../../modules/properties/services/attendees-service';
import {
  ActionItemPrioritySchema,
  type ActionItemStatus,
  type SerializedTodoItem,
} from '../../../../modules/properties/services/schema';
import { type ActionItem } from '../augment-schema';

export const AUGMENTED_TEXT_COLOR = 'var(--affine-text-secondary-color)';

export const COMMON_DELAYS = {
  PORTAL_DISPLAY: 1200,
} as const;

export function getLastNoteBlock(doc: Doc) {
  let note: NoteBlockModel | null = null;
  const root = doc.root;
  if (!root) return null;
  const { children } = root;
  for (let i = children.length - 1; i >= 0; i--) {
    const child = children[i];
    if (!child) continue;
    if (
      matchFlavours(child, ['affine:note']) &&
      child.displayMode !== NoteDisplayMode.EdgelessOnly
    ) {
      note = child as NoteBlockModel;
      break;
    }
  }
  return note;
}

export function demoteStrayEdgelessNotes(doc: Doc): boolean {
  const root = doc.root;
  if (!root) return false;
  let repaired = false;
  for (const child of root.children) {
    if (!matchFlavours(child, ['affine:note'])) continue;
    const note = child as NoteBlockModel;
    if (note.displayMode === NoteDisplayMode.EdgelessOnly) continue;
    if (note.displayMode === NoteDisplayMode.DocOnly) continue;
    doc.updateBlock(note, { displayMode: NoteDisplayMode.DocOnly });
    repaired = true;
  }
  return repaired;
}

export function ensureDocOnlyNote(doc: Doc): NoteBlockModel | null {
  const existing = getLastNoteBlock(doc);
  if (existing) {
    demoteStrayEdgelessNotes(doc);
    return existing;
  }
  const parentId = doc.root?.id;
  const id = doc.addBlock(
    'affine:note' as keyof BlockSuite.BlockModels,
    {
      displayMode: NoteDisplayMode.DocOnly,
    },
    parentId
  );
  return doc.getBlockById(id) as NoteBlockModel | null;
}

export function getBlockComponent<T extends Element = Element>(
  blockId: string,
  host?: EditorHost
): T | null {
  // Try host view first if available
  if (host?.view) {
    const component = host.view.getBlock(blockId) as T | null;
    if (component) return component;
  }

  // Fallback to DOM query
  const component = document.querySelector(
    `[data-block-id="${blockId}"]`
  ) as T | null;
  return component;
}

export function getLastParagraphBlock(host: EditorHost): BlockModel | null {
  const lastNote = getLastNoteBlock(host.doc);
  if (!lastNote) return null;

  for (let i = lastNote.children.length - 1; i >= 0; i--) {
    const child = lastNote.children[i];
    if (!child) continue;
    if (matchFlavours(child, ['affine:paragraph'])) {
      return child;
    }
  }

  return null;
}

export type { ActionItem } from '../augment-schema';

export function normalizeForComparison(text: string): string {
  return text.trim().toLowerCase().replace(/\s+/g, ' ');
}

export function isTextSubset(
  potentialSubset: string,
  fullText: string
): boolean {
  // Normalize Unicode characters first
  const normalizeUnicode = (text: string): string => {
    return text
      .normalize('NFD') // Normalize to decomposed form
      .replace(/[\u0300-\u036f]/g, '') // Remove diacritical marks
      .normalize('NFC'); // Normalize back to composed form
  };

  const subNorm = normalizeForComparison(normalizeUnicode(potentialSubset));
  const fullNorm = normalizeForComparison(normalizeUnicode(fullText));

  if (fullNorm.includes(subNorm)) {
    return true;
  }

  const subNoPunct = subNorm
    .replace(/[.,;:!?()[\]{}'""`~@#$%^&*+=|\\/<>-]/g, '')
    .trim();
  const fullNoPunct = fullNorm
    .replace(/[.,;:!?()[\]{}'""`~@#$%^&*+=|\\/<>-]/g, '')
    .trim();

  return fullNoPunct.includes(subNoPunct);
}

function tokenize(text: string): string[] {
  const matches = text.match(/[a-z0-9]+/g);
  return matches ? matches : [];
}

function commonPrefixRatio(a: string, b: string): number {
  const ta = tokenize(normalizeForComparison(a));
  const tb = tokenize(normalizeForComparison(b));
  const minLen = Math.min(ta.length, tb.length);
  if (minLen === 0) return 0;
  let i = 0;
  while (i < minLen) {
    const tokenA = ta[i];
    const tokenB = tb[i];
    if (tokenA === undefined || tokenB === undefined || tokenA !== tokenB)
      break;
    i++;
  }
  return i / minLen;
}

function jaccardSimilarity(a: string, b: string): number {
  const ta = new Set(tokenize(normalizeForComparison(a)));
  const tb = new Set(tokenize(normalizeForComparison(b)));
  if (ta.size === 0 || tb.size === 0) return 0;
  let inter = 0;
  for (const t of ta) if (tb.has(t)) inter++;
  const union = ta.size + tb.size - inter;
  return inter / union;
}

export function isNearDuplicateLine(
  newText: string,
  existingText: string
): boolean {
  const a = newText.trim();
  const b = existingText.trim();
  if (!a || !b) return false;
  if (isTextSubset(a, b) || isTextSubset(b, a)) return true;
  const prefix = commonPrefixRatio(a, b);
  if (prefix >= 0.6) return true;
  const taLen = tokenize(normalizeForComparison(a)).length;
  const tbLen = tokenize(normalizeForComparison(b)).length;
  const short = Math.min(taLen, tbLen) < 16;
  if (short && jaccardSimilarity(a, b) >= 0.75) return true;
  return false;
}

function normalizeActionItemIdentity(text: string): string {
  return normalizeForComparison(text)
    .replace(/\[[0-9]{2}:[0-9]{2}(?:-[0-9]{2}:[0-9]{2})?\]/g, ' ')
    .replace(/\(continued from \[[0-9]{2}:[0-9]{2}\]\)/g, ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function dedupeGeneratedActionItems(actionItems: ActionItem[]): ActionItem[] {
  const seen = new Set<string>();
  const uniqueActionItems: ActionItem[] = [];

  for (const item of actionItems) {
    const identity = normalizeActionItemIdentity(item.text);
    if (!identity || seen.has(identity)) continue;
    seen.add(identity);
    uniqueActionItems.push(item);
  }

  return uniqueActionItems;
}

export function resolveAssigneeOptionIds(
  assigneeEmails: readonly string[] | null | undefined,
  allowedOptionIds: ReadonlySet<string>
): string[] {
  if (!assigneeEmails) return [];
  const ids: string[] = [];
  const seen = new Set<string>();
  for (const email of assigneeEmails) {
    const norm = normalizeEmail(email);
    if (!norm || seen.has(norm) || !allowedOptionIds.has(norm)) continue;
    seen.add(norm);
    ids.push(norm);
  }
  return ids;
}

export const createActionItems = async (
  host: EditorHost,
  actionItems: ActionItem[],
  actionItemsService?: ActionItemsService,
  authService?: AuthService,
  workspacePropertiesAdapter?: WorkspacePropertiesAdapter,
  attendeesService?: AttendeesService
): Promise<DatabaseBlockModel | null> => {
  try {
    // Ensure there is a Doc-only note available for placement
    ensureDocOnlyNote(host.doc as Doc);
    actionItems = dedupeGeneratedActionItems(actionItems);

    const doc = host.doc;

    // If database is already present, skip creation
    const databaseBlocksAlreadyPresent =
      doc.getBlocksByFlavour('affine:database');
    if (databaseBlocksAlreadyPresent.length > 0) {
      return null;
    }

    // Declare variables that will be used in database creation scope
    let todoItems: SerializedTodoItem[] = [];
    let meAttendee: SerializedAttendee | null = null;

    // Prepare action items for saving if service is provided
    if (actionItemsService && actionItems.length > 0) {
      try {
        const pageId = doc.id;
        const pageTitle = doc.meta?.title || 'Untitled';

        // Calculate tomorrow's date for the default due date (store as ISO string for properties)
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        const tomorrowIso = new Date(
          tomorrow.getFullYear(),
          tomorrow.getMonth(),
          tomorrow.getDate()
        ).toISOString();

        // Create an attendee based on current user (if available)
        if (authService) {
          const currentUser = authService.session.account$.value;
          if (currentUser) {
            meAttendee = createAttendeeFromAuthUser({
              email: currentUser.email,
              label: currentUser.label,
              avatar: currentUser.avatar,
            });
          }
        }
        // Use AttendeesService (ground truth) for page attendees; fallback to adapter
        let attendees: SerializedAttendee[] = [];
        if (attendeesService) {
          attendees = attendeesService.getPageAttendees(pageId) || [];
        } else if (workspacePropertiesAdapter) {
          const pageAttendees =
            workspacePropertiesAdapter.getPageAttendees(pageId) || [];
          attendees = pageAttendees;
        }

        if (attendeesService) {
          const pageAttendees = attendeesService.getPageAttendees(pageId);
          const allowedEmails = new Set(
            pageAttendees.map(a => normalizeEmail(a.email || ''))
          );

          // Also include the current user as allowed
          if (meAttendee?.email) {
            allowedEmails.add(normalizeEmail(meAttendee.email));
          }

          actionItems.forEach(item => {
            if (item.assignTo && Array.isArray(item.assignTo)) {
              const uniq = new Set<string>();
              item.assignTo = item.assignTo.filter(email => {
                const normalized = normalizeEmail(email);
                const isAllowed =
                  allowedEmails.has(normalized) && !uniq.has(normalized);
                if (!isAllowed && !allowedEmails.has(normalized)) {
                  console.warn(
                    `Discarding AI-suggested attendee email that doesn't exist: ${email}`
                  );
                }
                if (isAllowed) uniq.add(normalized);
                return isAllowed;
              });
            }
          });
        }

        todoItems = actionItems.map(item => {
          let itemAssignees: SerializedAttendee[] = [];

          if (item.assignTo && item.assignTo.length > 0) {
            const emails = item.assignTo.map(e => normalizeEmail(e));
            itemAssignees = attendees.filter(a =>
              emails.includes(normalizeEmail(a.email || ''))
            );
          }

          const todoItem: SerializedTodoItem = {
            id: crypto.randomUUID(),
            title: item.text,
            pageId,
            pageTitle,
            databaseId: '', // Will be updated after database creation
            columnId: '',
            createdAt: new Date(), // Set current timestamp for proper sorting
            status: 'To Do' as ActionItemStatus,
            priority: ActionItemPrioritySchema.enum.Medium,
            // Initialize with filtered attendees or all if none specified
            assignedTo: itemAssignees.map(a => ({
              ...a,
              // Use normalized email as sole identifier for cross-system consistency
              id: normalizeEmail(a.email || ''),
              email: a.email || '',
              avatar: a.avatar === null ? undefined : a.avatar,
            })),
            progress: 10,
            dueDate: tomorrowIso,
            closeLoop: '',
            context: item.context || '',
            breakdownSteps: Array.isArray(item.breakdownSteps)
              ? item.breakdownSteps.map(s => ({
                  id: s.id,
                  title: s.title,
                  description: s.description,
                  completed: false,
                }))
              : undefined,
          };

          return todoItem;
        });

      } catch (error) {
        console.error(
          'Error saving action items to workspace properties:',
          error
        );
      }
    }

    const noteId = getLastNoteBlock(doc)?.id;
    if (!noteId) return null; // should not happen due to ensureDocOnlyNote, but keep safe

    // Create paragraph blocks for each action item
    const actionItemBlocks: BlockModel[] = [];
    const highlightedCardIds: string[] = [];
    const actionItemByBlockId = new Map<string, ActionItem>();
    const meEmail = meAttendee?.email ? normalizeEmail(meAttendee.email) : null;
    actionItems.forEach(item => {
      const text = new doc.Text(item.text);
      const blockId = doc.addBlock(
        'affine:paragraph',
        { type: 'text', text },
        noteId
      );
      const block = doc.getBlockById(blockId) as BlockModel & {
        text?: {
          format: (start: number, length: number, attrs: object) => void;
          length?: number;
        };
      };
      if (block) {
        actionItemBlocks.push(block);
        actionItemByBlockId.set(block.id, item);
        const assignees = item.assignTo?.map(e => normalizeEmail(e)) ?? [];
        const isAssignedToMe = meEmail && assignees.includes(meEmail);
        if (isAssignedToMe) {
          highlightedCardIds.push(blockId);
        }
        if (!isAssignedToMe && block.text) {
          block.text.format(0, block.text.length ?? 0, {
            color: AUGMENTED_TEXT_COLOR,
          });
        }
      }
    });

    if (actionItemBlocks.length === 0) return null;

    const firstActionItemBlock = actionItemBlocks[0];
    if (!firstActionItemBlock) return null;

    const parentModel = doc.getParent(firstActionItemBlock);
    if (!parentModel) return null;

    // Create database block at end of note (action item blocks will be moved into it)
    const databaseTitle = new doc.Text('Action Items');
    const databaseId = doc.addBlock(
      'affine:database',
      { title: databaseTitle },
      parentModel.id
    );

    // Get database model
    const database = doc.getBlockById(databaseId) as DatabaseBlockModel;
    if (!database) return null;

    // Set the action items flag after creation
    database.isActionItemsDatabase = true;

    // Highlight cards assigned to the current user with brand color border
    if (highlightedCardIds.length > 0) {
      database.highlightedCardIds = highlightedCardIds;
    }

    const todoId = doc.generateBlockId();
    const inProgressId = doc.generateBlockId();
    const doneId = doc.generateBlockId();
    const highPriorityId = doc.generateBlockId();
    const mediumPriorityId = doc.generateBlockId();
    const lowPriorityId = doc.generateBlockId();
    const titleColumnId = doc.generateBlockId();
    const statusColumnId = doc.generateBlockId();
    const priorityColumnId = doc.generateBlockId();
    const dueDateColumnId = doc.generateBlockId();
    const assignToColumnId = doc.generateBlockId();
    const closeLoopColumnId = doc.generateBlockId();
    const contextColumnId = doc.generateBlockId();
    const remindLaterColumnId = doc.generateBlockId();

    if (actionItemsService && todoItems.length > 0) {
      todoItems = todoItems.flatMap((todoItem, index) => {
        const block = actionItemBlocks[index];
        return block
          ? [
              {
                ...todoItem,
                id: block.id,
                databaseId: database.id,
                columnId: titleColumnId,
              },
            ]
          : [];
      });

      try {
        actionItemsService.beginBatch();
        try {
          actionItemsService.removePageActionItems(doc.id);
          todoItems.forEach((todoItem, index) => {
            const originalActionItem = actionItems[index];
            actionItemsService.createItem({
              id: todoItem.id,
              title: todoItem.title,
              pageId: todoItem.pageId,
              pageTitle: todoItem.pageTitle,
              databaseId: todoItem.databaseId,
              columnId: todoItem.columnId,
              status: todoItem.status,
              progress: todoItem.progress,
              assignedTo: todoItem.assignedTo,
              remindLater: todoItem.remindLater,
              dueDate: todoItem.dueDate as string,
              closeLoop: todoItem.closeLoop,
              context: todoItem.context,
              createdAt: todoItem.createdAt as Date,
              breakdownSteps: Array.isArray(originalActionItem?.breakdownSteps)
                ? originalActionItem.breakdownSteps.map(s => ({
                    id: s.id,
                    title: s.title,
                    description: s.description,
                    completed: false,
                  }))
                : undefined,
            });
          });
        } finally {
          actionItemsService.endBatch();
        }
      } catch (error) {
        console.error(
          'Error saving action items to workspace properties:',
          error
        );
      }
    }

    // First set up the database structure
    doc.transact(() => {
      // Create attendee options for the database column using page attendees only
      const pageAttendeesForOptions: SerializedAttendee[] = attendeesService
        ? attendeesService.getPageAttendees(doc.id)
        : workspacePropertiesAdapter?.getPageAttendees(doc.id) || [];
      const attendeeOptions: Array<{
        id: string;
        value: string;
        color: string;
        avatar?: string | null;
        displayName?: string;
      }> = [];
      const seenEmails = new Set<string>();
      for (const a of pageAttendeesForOptions) {
        const email = (a.email || '').trim();
        if (!email) continue;
        const norm = normalizeEmail(email);
        if (seenEmails.has(norm)) continue;
        seenEmails.add(norm);
        // Display strictly the email to match AttendeesService UI expectations
        const displayValue = a.email || '';
        attendeeOptions.push({
          id: norm,
          value: displayValue,
          color: 'var(--affine-tag-purple)',
          avatar: a.avatar ?? null,
          displayName: a.name || undefined,
        });
      }

      database.columns = [
        {
          id: titleColumnId,
          type: 'title',
          name: 'Title',
          data: {},
        },
        {
          id: statusColumnId,
          type: 'select', // THIS IS CRITICAL - must match the groupBy.name below
          name: 'Status',
          data: {
            options: [
              { id: todoId, value: 'To Do', color: 'var(--affine-tag-blue)' }, // Blue tag color
              {
                id: inProgressId,
                value: 'In Progress',
                color: 'var(--affine-tag-purple)',
              }, // Purple tag color
              { id: doneId, value: 'Done', color: 'var(--affine-tag-green)' }, // Green tag color
            ],
          },
        },
        // Progress column removed to ensure proper schema mapping
        {
          id: assignToColumnId,
          type: 'multi-select',
          name: "Who's Responsible?",
          data: {
            options: attendeeOptions,
          },
        },
        {
          id: priorityColumnId,
          type: 'select',
          name: 'Priority',
          data: {
            options: [
              {
                id: highPriorityId,
                value: 'High',
                color: 'var(--affine-tag-red)',
              }, // Red tag color
              {
                id: mediumPriorityId,
                value: 'Medium',
                color: 'var(--affine-tag-orange)',
              }, // Orange tag color
              {
                id: lowPriorityId,
                value: 'Low',
                color: 'var(--affine-tag-green)',
              }, // Green tag color
            ],
          },
        },
        {
          id: dueDateColumnId,
          type: 'date',
          name: 'Due Date',
          data: {},
        },
        {
          id: contextColumnId,
          type: 'rich-text',
          name: 'Context',
          data: {},
        },
        {
          id: closeLoopColumnId,
          type: 'rich-text',
          name: 'My Notes',
          data: {},
        },
        {
          id: remindLaterColumnId,
          type: 'multi-select',
          name: 'Remind Later',
          data: {
            options: [
              {
                id: 'three_hours',
                value: 'three_hours',
                color: 'var(--affine-tag-blue)',
              },
              {
                id: 'tomorrow',
                value: 'tomorrow',
                color: 'var(--affine-tag-purple)',
              },
            ],
          },
        },
      ];

      // Initialize with a kanban view
      database.views = [
        {
          id: doc.generateBlockId(),
          name: 'Default View',
          mode: 'kanban',
          columns: [
            { id: titleColumnId, hide: false },
            { id: statusColumnId, hide: false },
            { id: assignToColumnId, hide: false },
            { id: priorityColumnId, hide: false },
            { id: dueDateColumnId, hide: false },
            { id: contextColumnId, hide: true },
            { id: closeLoopColumnId, hide: true },
            { id: remindLaterColumnId, hide: true },
          ],
          filter: {
            type: 'group',
            op: 'and',
            conditions: [],
          },
          // Group by Status for Kanban columns
          groupBy: {
            type: 'groupBy',
            columnId: statusColumnId,
            name: 'select',
          },
          header: {
            titleColumn: titleColumnId,
            iconColumn: 'type',
          },
          groupProperties: [],
        },
      ];

      database.cells = {};

      const allowedOptionIds = new Set(attendeeOptions.map(opt => opt.id));

      actionItemBlocks.forEach(block => {
        const sourceItem = actionItemByBlockId.get(block.id);
        const assigneeIds = resolveAssigneeOptionIds(
          sourceItem?.assignTo,
          allowedOptionIds
        );

        database.cells[block.id] = {
          [statusColumnId]: {
            columnId: statusColumnId,
            value: todoId,
          },
          [assignToColumnId]: {
            columnId: assignToColumnId,
            value: assigneeIds,
          },
          [closeLoopColumnId]: {
            columnId: closeLoopColumnId,
            value: new doc.Text(),
          },
          [contextColumnId]: {
            columnId: contextColumnId,
            value: new doc.Text(sourceItem?.context || ''),
          },
        };

        // Add the priority cell (default to Medium; explicit edits happen via DB/UI or service)
        {
          const optId = mediumPriorityId;
          const blockCells = database.cells[block.id];
          if (blockCells) {
            blockCells[priorityColumnId] = {
              columnId: priorityColumnId,
              value: optId,
            };
          }
        }

        // Calculate tomorrow's date and add due date cell
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);

        const blockCellsForDue = database.cells[block.id];
        if (blockCellsForDue) {
          blockCellsForDue[dueDateColumnId] = {
            columnId: dueDateColumnId,
            value: tomorrow.getTime(),
          };
        }

        try {
          const steps = sourceItem?.breakdownSteps;
          const dbWithSteps = database as DatabaseBlockModel & {
            setBreakdownSteps?: (
              blockId: string,
              steps: Array<{
                id: string;
                title: string;
                description: string;
                points: number;
                completed: boolean;
              }>
            ) => void;
          };
          if (
            Array.isArray(steps) &&
            steps.length > 0 &&
            typeof dbWithSteps.setBreakdownSteps === 'function'
          ) {
            const normalized = steps.map(s => ({
              id: s.id,
              title: s.title,
              description: s.description,
              points: 15,
              completed: false,
            }));
            dbWithSteps.setBreakdownSteps(block.id, normalized);
          }
        } catch {
          // noop
        }
      });
    });

    // Now move the blocks into the database
    doc.moveBlocks(actionItemBlocks, database);

    // Wait for everything to process
    await new Promise(resolve => setTimeout(resolve, 100));

    // Force a refresh
    doc.captureSync();

    if (actionItemsService && database) {
      setTimeout(() => {
        try {
          const databaseComponent = host.std.view.getBlock(
            database.id
          ) as DatabaseBlockComponent;
          if (databaseComponent) {
            actionItemsService.registerDatabaseBlock(doc.id, databaseComponent);
          }
        } catch (error) {
          console.error(
            'Error registering database block with ActionItemsService:',
            error
          );
        }
      }, 500);
    }

    return database;
  } catch (error) {
    console.error('Error creating Kanban board:', error);
    return null;
  }
};

export function gracefulDelay(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise(resolve => {
    if (signal?.aborted) {
      resolve();
      return;
    }

    const timeoutId = setTimeout(() => {
      resolve();
    }, ms);

    if (signal) {
      const abortHandler = () => {
        clearTimeout(timeoutId);
        resolve();
      };

      signal.addEventListener('abort', abortHandler, { once: true });
    }
  });
}

export function createLogger(prefix: string) {
  return {
    log: (_message: string, ..._args: unknown[]) => {
      // Logging disabled
    },
    warn: (message: string, ...args: unknown[]) => {
      console.warn(`[${prefix}] ${message}`, ...args);
    },
    error: (message: string, ...args: unknown[]) => {
      console.error(`[${prefix}] ${message}`, ...args);
    },
  };
}

export function isAbortError(error: unknown): boolean {
  return (
    error instanceof Error &&
    (error.name === 'AbortError' ||
      error.message.includes('aborted') ||
      error.message.includes('cancelled'))
  );
}

export function createTimer() {
  const startTime = performance.now();

  return {
    elapsed: () => performance.now() - startTime,
    elapsedMs: () => Math.round(performance.now() - startTime),
    toString: () => `${(performance.now() - startTime).toFixed(2)}ms`,
  };
}

function findBlocksByTimestamps(
  host: EditorHost,
  timestamps: string[]
): string[] {
  const doc = host.doc;
  const timestampSet = new Set(timestamps);
  const matchedBlockIds: string[] = [];

  const paragraphBlocks = doc.getBlocksByFlavour('affine:paragraph');
  const listBlocks = doc.getBlocksByFlavour('affine:list');
  const allBlocks = [...paragraphBlocks, ...listBlocks];

  for (const blockEntry of allBlocks) {
    const blockId =
      (blockEntry as { id?: string }).id ??
      (blockEntry as { model?: { id?: string } }).model?.id ??
      '';
    if (!blockId) continue;

    const block = doc.getBlockById(blockId) as {
      timestamps?: { toString(): string };
    } | null;
    const blockTimestamp = block?.timestamps?.toString?.() ?? '';
    if (blockTimestamp && timestampSet.has(blockTimestamp)) {
      matchedBlockIds.push(blockId);
    }
  }

  return matchedBlockIds;
}

let currentSlotDisposable: { dispose: () => void } | null = null;

export function initializeLinkedBlocksListener(
  getHost: () => EditorHost | null
): () => void {
  if (currentSlotDisposable) {
    currentSlotDisposable.dispose();
    currentSlotDisposable = null;
  }

  import('../../block-suite-editor/specs/custom/pique/pique-slots')
    .then(({ highlightLinkedBlocksSlot }) => {
      currentSlotDisposable = highlightLinkedBlocksSlot.on(({ timestamps }) => {
        const host = getHost();
        if (!host) return;
        if (!timestamps || timestamps.length === 0) return;

        const validBlockIds = findBlocksByTimestamps(host, timestamps);
        if (validBlockIds.length === 0) return;

        const firstBlockId = validBlockIds[0];
        if (!firstBlockId) return;
        const blockComponent = getBlockComponent(firstBlockId, host);

        if (blockComponent) {
          blockComponent.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });

          try {
            if (host.std?.selection) {
              const blockSelections = validBlockIds.map(id =>
                host.std.selection.create('block', { blockId: id })
              );
              host.std.selection.setGroup('note', blockSelections);
            }
          } catch {}
        }
      });
    })
    .catch(() => {});

  return () => {
    if (currentSlotDisposable) {
      currentSlotDisposable.dispose();
      currentSlotDisposable = null;
    }
  };
}
